import React, { FunctionComponentElement, Ref, cloneElement } from 'react'
import { RegisterOptions, useFormContext } from 'react-hook-form'

import { useTranslate } from '@hozana/intl/useTranslate'
import { mergeRefs } from '@hozana/utils/functions/refs'

import { FormError } from 'elements/input/FormError'
import { InputLabel, TInputLabelProps } from 'elements/input/InputLabel'
import { Cell, TCellProps } from 'elements/layout/Cell'
import { Row } from 'elements/layout/Row'
import { Transition } from 'elements/motion/Transition'
import { P } from 'elements/text/P'

type TInputGroupProps<FormData extends Record<string, any>> = TCellProps & {
  /**
   * The name of the input, used for `name` and `id` attributes.
   * Children's `name` prop overrides this one.
   */
  name: keyof FormData
  /**
   * The label of the input, used for `aria-label` attribute and
   * displayed above the input.
   */
  label?: string
  /**
   * A description about the input to be displayed under the label.
   */
  fieldDescription?: JSX.Element
  /**
   * The options to pass to the `register` method.
   * @default {}
   */
  options?: TValueOrArrayOfValues<RegisterOptions<FormData>>
  /**
   * The input itself.
   */
  children: JSX.Element
  /**
   * Hint text to display somewhere around the input.
   */
  hint?: string
  /**
   * Display hint below input
   */
  bottomHint?: boolean
} & Pick<TInputLabelProps, 'color'>

/**
 * Wrapper that adds a label, hint and error message around an input.
 * Expected to be used with `react-hook-form`, uncontrolled by default.
 */
export const InputGroup = <FormData extends Record<string, any>>({
  name,
  label,
  color,
  fieldDescription,
  options = [],
  children,
  hint,
  bottomHint,
  ...otherProps
}: TInputGroupProps<FormData>): JSX.Element => {
  const { formState, register } = useFormContext()
  const optionsArray = Array.isArray(options) ? options : [options]
  const errorMessage = formState.errors[name]?.message
  const translatedErrorMessage = useTranslate(errorMessage)

  const required = optionsArray.some((option) => 'required' in option)
  const i18nLabel = useTranslate(label)

  const child = React.Children.only<FunctionComponentElement<TAnyProps & { ref: Ref<HTMLElement> }>>(children)
  const { ref = undefined, ...registerRest } = child.props.onChange
    ? {} // Do not register a controlled input
    : register(
        children.props.name ?? name,
        (optionsArray as Omit<RegisterOptions<FormData>, 'valueAsDate' | 'valueAsNumber'>[]).reduce(
          (acc, options) => ({ ...acc, ...options }),
          {},
        ),
      )
  const extendedChild = cloneElement(child, {
    'aria-errormessage': translatedErrorMessage ?? undefined,
    'aria-invalid': !!translatedErrorMessage,
    'aria-label': label,
    name: children.props.name ?? name,
    id: children.props.name ?? name,
    required,
    ...registerRest,
    ...children.props,
    ref: mergeRefs(child.ref, ref),
  })

  return (
    <Cell w="100%" column align="stretchTop" style={{ gap: '0.25rem' }} {...otherProps}>
      {label && (
        <InputLabel color={color} htmlFor={children.props.name ?? name} required={required}>
          {i18nLabel}
        </InputLabel>
      )}
      {fieldDescription}
      <Row column={bottomHint}>
        <Cell
          w={1}
          {...(hint &&
            !bottomHint && {
              w: { xs: 1, sm: 6 / 10 },
              p: '0 5px 0 0',
            })}
        >
          {extendedChild}
        </Cell>

        {hint && (
          <Cell
            w={1}
            {...(!bottomHint && {
              w: { xs: 1, sm: 4 / 10 },
              p: '0 0 0 5px',
            })}
            opacity={0.6}
          >
            <P italic color={color} align={bottomHint ? 'right' : 'left'} fontSize="13px" p="5px">
              {hint}
            </P>
          </Cell>
        )}
      </Row>
      <Transition active={translatedErrorMessage} initial={{ height: '0px' }} final={{ height: '100px' }}>
        {translatedErrorMessage && (
          <FormError m={hint && bottomHint ? '0 0 10px 0' : '10px 0'}>{translatedErrorMessage}</FormError>
        )}
      </Transition>
    </Cell>
  )
}
